/**
 * 颜色平衡（对比 Photoshop 补全第一档缺口 阶段 1；`document/types.ts` 里
 * `colorBalance` 这个 kind 之前只占了类型联合的位置，从 `resolve.ts` 到
 * 面板全部零实现，这次补上）。
 *
 * 阴影/中间调/高光三段权重是按亮度的三角权重（跟 `engine/effects/render.ts`
 * 的 `toneRangeWeight` 思路一致，但那是渲染层文件，分层规则不允许这里
 * 反向 import，本地重写一份等价公式——三角形权重：阴影在 l=0 满权、
 * l=0.5 归零；高光在 l=1 满权、l=0.5 归零；中间调补足剩下的权重，三者
 * 在任意 l 上恒和为 1）。
 */
export interface ColorBalanceParams {
  readonly shadowR: number;
  readonly shadowG: number;
  readonly shadowB: number;
  readonly midR: number;
  readonly midG: number;
  readonly midB: number;
  readonly highR: number;
  readonly highG: number;
  readonly highB: number;
  /** 保留明度，0 或 1。 */
  readonly preserveLuminosity: number;
}

export const DEFAULT_COLOR_BALANCE_PARAMS: ColorBalanceParams = {
  shadowR: 0,
  shadowG: 0,
  shadowB: 0,
  midR: 0,
  midG: 0,
  midB: 0,
  highR: 0,
  highG: 0,
  highB: 0,
  preserveLuminosity: 1,
};

function shadowWeight(l: number): number {
  return Math.min(1, Math.max(0, 1 - l * 2));
}

function highlightWeight(l: number): number {
  return Math.min(1, Math.max(0, l * 2 - 1));
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function clampByte(v: number): number {
  return Math.round(Math.min(255, Math.max(0, v)));
}

export function applyColorBalance(
  r: number,
  g: number,
  b: number,
  params: ColorBalanceParams,
): [number, number, number] {
  const l = luminance(r, g, b) / 255;
  const ws = shadowWeight(l);
  const wh = highlightWeight(l);
  const wm = 1 - ws - wh;

  // 滑杆 −100..100 换算成像素位移，±100 时最多挪 ±127.5——够明显但不会
  // 单个色段就把两端直接冲到 0/255
  const shift = (shadow: number, mid: number, high: number): number =>
    (ws * shadow + wm * mid + wh * high) * 1.275;

  let outR = r + shift(params.shadowR, params.midR, params.highR);
  let outG = g + shift(params.shadowG, params.midG, params.highG);
  let outB = b + shift(params.shadowB, params.midB, params.highB);

  if (params.preserveLuminosity !== 0) {
    const before = luminance(r, g, b);
    const after = luminance(outR, outG, outB);
    if (after > 1e-6) {
      const scale = before / after;
      outR *= scale;
      outG *= scale;
      outB *= scale;
    }
  }

  return [clampByte(outR), clampByte(outG), clampByte(outB)];
}
